'use client';

import Modal from '@frontend/jetlend-web-ui/src/ui/modals/Modal';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useDispatch } from 'react-redux';
import Button from '@frontend/jetlend-web-ui/src/ui/inputs/Button/Button';
import useModalHandler from '@frontend/jetlend-core/src/hooks/useModalHandler';
import useSimpleApiHandler from '@frontend/jetlend-core/src/hooks/useSimpleApiHandler';
import logo from '@frontend/jetlend-assets/icons/jetlend-logo.png';
import {
    useTransition,
    animated,
    useSpringRef,
} from '@react-spring/web';
import {
    achievementModalHandler,
    achievementViewedSaga,
    getAchievementsHandler,
} from '@app/ducks/academy/course';
import iconBackground from '@app/icons/academy/icon-background.png';
import Image from '@app/components/Image/Image';
import AchievementShareButtonsContainer from '../AchievementShareButtons/AchievementShareButtonsContainer';

import styles from './achievementPopup.module.scss';

const DEFAULT_ACHIEVED_DESCRIPTION = 'Начинаю писать свою собственную историю успеха на рынке краудлендинга!';

/**
 * Модальное окно с информацией о ачивке
 * Открывается сразу после получения награды
 */
const AchievementPopup = () => {
    const dispatch = useDispatch();
    const [isOpen, , close] = useModalHandler(achievementModalHandler);
    const [achievements] = useSimpleApiHandler(getAchievementsHandler);
    const [currentIndex, setCurrentIndex] = useState(0);

    const notSeenAchievements = useMemo(() => achievements?.filter(achievement => achievement?.show), [achievements]);
    const currentAchievementInfo = useMemo(() => notSeenAchievements?.[currentIndex], [currentIndex, notSeenAchievements]);
    const isLastAchievement = currentIndex === notSeenAchievements?.length - 1;

    const transitionRef = useSpringRef();
    // Анимация для перелистывания карточек с ачивками
    // Добавляется эффект плавного скролла
    const transitions = useTransition(currentAchievementInfo, {
        ref: transitionRef,
        keys: null,
        from: {
            opacity: 0,
            transform: 'translate(100%, 0)',
        },
        enter: {
            opacity: 1,
            transform: 'translate(0%, 0)',
        },
        leave: {
            opacity: 0,
            transform: 'translate(-100%, 0)',
        },
    });

    const didNextAchievementInfoClicked = useCallback(() => {
        setCurrentIndex(prev => prev + 1);
        dispatch(achievementViewedSaga({ id: currentAchievementInfo?.id }));
    }, [currentAchievementInfo, dispatch]);

    const didCloseModalClicked = useCallback(() => {
        dispatch(achievementViewedSaga({ id: currentAchievementInfo?.id }));
        close();
    }, [currentAchievementInfo, dispatch]);

    useEffect(() => {
        transitionRef.start();
    }, [currentIndex]);

    return (
        <Modal
            title=""
            noBorder
            closable
            size="xsmall"
            isOpen={isOpen}
            onClosed={didCloseModalClicked}
            contentClassName={styles['modal-content']}
        >
            <div className={styles['animated-wrapper']}>
                {transitions((style, item) => (
                    <animated.div className={styles['animated-item']} style={currentIndex > 0 ? style : {}} key={item?.id}>
                        <div className={styles['text']}>{item?.achieved_title ?? item?.description}</div>
                        <div className={styles['icon-wrapper']} style={{ backgroundColor: item?.background_color }}>
                            <Image src={iconBackground} alt="" fill />
                            <div className={styles['title']}>{item?.title}</div>
                            <div className={styles['icon']}>
                                <Image src={item?.icon} alt="" fill />
                            </div>
                            <div className={styles['text-white']}>{item?.preview_description}</div>
                            <div className={styles['text-small']}>{item?.achieved_description ?? DEFAULT_ACHIEVED_DESCRIPTION}</div>
                            <div className={styles['logo']}>
                                <Image src={logo} alt="" fill />
                            </div>
                        </div>
                    </animated.div>
                ))}
                <div className={styles['buttons']}>
                    <Button
                        text={isLastAchievement ? 'Закрыть' : 'Дальше'}
                        onClick={isLastAchievement ? didCloseModalClicked : didNextAchievementInfoClicked}
                        size="small"
                        containerClassName={styles['button-container']}
                        className={styles['button']}
                    />
                    <AchievementShareButtonsContainer
                        icon={currentAchievementInfo?.icon}
                        referral_link={currentAchievementInfo?.referral_link}
                        referral_title={currentAchievementInfo?.referral_title}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default AchievementPopup;
